import { Cell, CellWithHierarchy } from '@pelckmans/elementis/components/table';
import { StudentAssignment } from '../api/AssignmentStudentApi';
import { useContext } from 'react';
import ModuleContext from '../../../material/_contexts/ModuleContext';
import Avatar from '@pelckmans/elementis/components/avatar';
import AvatarImage from '@pelckmans/elementis/components/avatar/avatar-image';
import { useTranslation } from 'react-i18next';
import { formatAssignmentToDate } from '../../../../utils/formatDate';
import { MaterialIcon } from '../../../material/components/MaterialIcon';
import { EndDateCategory } from './AssignmentDivision';
import Results from '@pelckmans/elementis/components/results';
import { getResultPercentages } from '../../../../utils/results';
import { getFullHierarchyDescriptionOfNode } from '../../../../utils/getNodeDescription';

const BASE = 'PAGES.ASSIGNMENTS.STUDENT.SECTION.TABLE.BODY.ASSIGNMENT';

type AssignmentProps = {
  assignment: StudentAssignment;
  endDateCategory: EndDateCategory;
};

export default function Assignment({ assignment, endDateCategory }: AssignmentProps) {
  const { t } = useTranslation();
  const { module, subModules } = useContext(ModuleContext);

  const assignmentModule = [module, ...subModules].find(m => m!.id === assignment.module);

  const now = new Date();
  const from = new Date(assignment.from);

  const pillProminence =
    endDateCategory === 'TODAY' ? 'danger' : endDateCategory === 'THIS_WEEK' ? 'warning' : 'neutral';

  const period =
    from < now
      ? t(`${BASE}.PERIOD.TO`, { to: formatAssignmentToDate(assignment.to) })
      : t(`${BASE}.PERIOD.FROM_TO`, {
          from: formatAssignmentToDate(assignment.from),
          to: formatAssignmentToDate(assignment.to),
        });

  const locationIndicator = getFullHierarchyDescriptionOfNode(assignment.hierarchy, t);

  return (
    <tr className="table-item">
      <CellWithHierarchy hierarchyProps={{ level: 1 }}>
        <Cell.Text
          title={assignment.name}
          startAdornment={<MaterialIcon materialType={assignment.materialType} contentType={assignment.contentType} />}
        />
      </CellWithHierarchy>
      <Cell>
        {assignmentModule && (
          <Cell.Text
            title={assignmentModule.name}
            subTitleBot={locationIndicator}
            startAdornment={
              <Avatar>
                <AvatarImage name={assignmentModule.name} src={assignmentModule.styling?.logo || ''} />
              </Avatar>
            }
          />
        )}
      </Cell>
      <Cell
        pillGroupProps={{
          'aria-label': t(`${BASE}.PERIOD.A11Y_LABEL`),
          role: 'group',
        }}
      >
        <Cell.Pill prominence={pillProminence}>{period}</Cell.Pill>
      </Cell>
      <Cell>
        <Results
          quantifiable
          status={assignment.score.status}
          showTooltip
          variant="full"
          {...getResultPercentages(assignment.score, false)}
        />
      </Cell>
      <Cell>
        <Cell.Text title={assignment.repeatable ? t(`${BASE}.ATTEMPTS.MULTIPLE`) : t(`${BASE}.ATTEMPTS.ONE`)} />
      </Cell>
    </tr>
  );
}
